import { parseMoneyInputToCents } from "@/lib/money";
import type { SettlementLineRow } from "@/services/adminSettlementService";
import { settlementInvoiceReference } from "../../settlementUi";

export type RegisteredSupplierInvoice = {
  documentId: string;
  reference: string | null;
  invoicedAt: string | null;
};

/** One entry per supplier invoice document already recorded on the lines. */
export function registeredInvoicesFromLines(
  lines: SettlementLineRow[],
): RegisteredSupplierInvoice[] {
  const byId = new Map<string, RegisteredSupplierInvoice>();
  for (const line of lines) {
    if (line.invoice_status !== "INVOICED" || !line.invoice_document_id)
      continue;
    if (byId.has(line.invoice_document_id)) continue;
    byId.set(line.invoice_document_id, {
      documentId: line.invoice_document_id,
      reference: settlementInvoiceReference(line),
      invoicedAt: line.invoiced_at,
    });
  }
  return [...byId.values()];
}

export type SupplierInvoiceTotalError = "required" | "invalid" | "mismatch";

export type SupplierInvoiceTotalCheck =
  | { ok: true; totalCents: number }
  | { ok: false; error: SupplierInvoiceTotalError };

/**
 * The typed invoice total must match what we owe for the selected lines.
 * Without an expected amount (nothing invoiceable) any valid total is accepted.
 */
export function checkSupplierInvoiceTotal(
  rawTotal: string,
  expectedCents: number,
): SupplierInvoiceTotalCheck {
  if (!rawTotal.trim()) return { ok: false, error: "required" };
  const totalCents = parseMoneyInputToCents(rawTotal);
  if (totalCents === null) return { ok: false, error: "invalid" };
  if (expectedCents > 0 && totalCents !== expectedCents) {
    return { ok: false, error: "mismatch" };
  }
  return { ok: true, totalCents };
}
